import type { HistoryDays, TeamHistoryDto } from '@internal/scorecards-common';
import { useEffect, useReducer } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { scorecardsApiRef } from '../api/ScorecardsApi';

type State = { loading: boolean; error?: Error; data?: TeamHistoryDto };
type Action =
  | { type: 'FETCH_START' }
  | { type: 'FETCH_SUCCESS'; data: TeamHistoryDto }
  | { type: 'FETCH_ERROR'; error: Error };

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'FETCH_START':
      return { ...state, loading: true }; // keep stale data visible during re-fetch
    case 'FETCH_SUCCESS':
      return { loading: false, data: action.data };
    case 'FETCH_ERROR':
      return { loading: false, error: action.error };
    default:
      return state;
  }
}

export function useTeamHistory(
  team: string,
  days: HistoryDays,
  trackIds?: string[],
): { loading: boolean; error?: Error; data?: TeamHistoryDto } {
  const api = useApi(scorecardsApiRef);
  const [state, dispatch] = useReducer(reducer, { loading: true });

  // Stable serialisation so the effect only re-runs when the set of IDs changes.
  const trackIdsKey = trackIds?.slice().sort().join(',') ?? '';

  useEffect(() => {
    let cancelled = false;
    dispatch({ type: 'FETCH_START' });

    api
      .getTeamHistory(team, days, trackIds?.length ? trackIds : undefined)
      .then(data => {
        if (!cancelled) dispatch({ type: 'FETCH_SUCCESS', data });
      })
      .catch(err => {
        if (!cancelled)
          dispatch({
            type: 'FETCH_ERROR',
            error: err instanceof Error ? err : new Error(String(err)),
          });
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, team, days, trackIdsKey]);

  return state;
}
